// Shared motion presets for the app — single source of truth for timing, easing
// and variants used with framer-motion. Keeps pages from scattering arbitrary
// durations and hardcoded transforms. Respects prefers-reduced-motion.
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";

// Design tokens for motion. Mirror these in CSS where a transition is CSS-only.
export const EASE = {
  out: [0.22, 1, 0.36, 1],   // decelerate — entrances
  inOut: [0.65, 0, 0.35, 1], // symmetric — layout
  soft: [0.2, 0.7, 0.2, 1],  // gentle — hover/expand
};
export const DUR = { fast: 0.18, base: 0.32, slow: 0.5 };

export const fadeInUp = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: DUR.base, ease: EASE.out } },
};
export const fadeIn = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: DUR.base, ease: EASE.out } },
};
export const popIn = {
  hidden: { opacity: 0, scale: 0.96 },
  show: { opacity: 1, scale: 1, transition: { duration: DUR.base, ease: EASE.out } },
};
export const listContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05, delayChildren: 0.02 } },
};
export const listItem = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: DUR.base, ease: EASE.out } },
  exit: { opacity: 0, y: -4, transition: { duration: DUR.fast, ease: EASE.out } },
};

// Reduced-motion-safe variants: cross-fade only, no transforms, near-instant.
const instant = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.001 } },
  exit: { opacity: 0, transition: { duration: 0.001 } },
};
const noStagger = { hidden: {}, show: {} };

// Hook returning the right preset set for the current motion preference.
export function useMotionPresets() {
  const reduce = useReducedMotion();
  if (reduce) {
    return {
      reduce: true,
      fadeInUp: instant,
      fadeIn: instant,
      popIn: instant,
      listContainer: noStagger,
      listItem: instant,
      tap: {},
    };
  }
  return {
    reduce: false,
    fadeInUp,
    fadeIn,
    popIn,
    listContainer,
    listItem,
    tap: { whileTap: { scale: 0.97 } },
  };
}

// shadcn Button with built-in press feedback. Honours reduced motion because
// framer-motion skips whileTap transforms under the global reduced setting at
// the DOM level, but we also no-op the prop via useMotionPresets where needed.
export const MotionButton = motion.create(Button);
